import React, { useState, useCallback, useRef, memo, ChangeEvent } from "react";
import { useAppDispatch, useAppSelector } from "stores";
import styled from "styled-components";
import store from "stores/interfaces";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  Popover,
  ButtonBase,
  List,
  Divider,
  InputBase,
  IconButton,
  Box,
  Typography,
  Avatar,
} from "@mui/material";
import FaceIcon from "@mui/icons-material/Face";
import CharacterNameSelectItem from "./CharacterNameSelectItem";
import { useToCDNUrl } from "hooks/useCDN";
import { appStateMutate } from "stores/modules/app.state/operations";
import { appChatStateMutate } from "stores/modules/app.chat/operations";
import { Cancel } from "@mui/icons-material";
import theme from "theme";
// import CharacterAutoComplete from "./CharacterAutoComplete";

const CharacterNameSelect = () => {
  const dispatch = useAppDispatch();
  const [t] = useTranslation();
  const toCDNUrl = useToCDNUrl();

  const anchorEl = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const name = useAppSelector(store.getCurrentRoomChatName);
  const activeCharacterIds = useAppSelector(store.getMyRoomActiveCharacterIds);
  const inactiveCharacterIds = useAppSelector(
    store.getMyRoomIncativeCharacterIds
  );
  const isRoleAudience = useAppSelector(store.getIsRoleAudience);
  const currentRoomChatCharacter = useAppSelector(
    store.getCurrentRoomChatCharacter
  );
  const isOverride = useAppSelector(store.getIsOverride);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      dispatch(
        appStateMutate((state) => {
          state.roomChatName = e.currentTarget.value;
          state.roomChatId = "";
        })
      );
    },
    [dispatch]
  );

  const onClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);
  const onOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);
  const onClearoverrideCharacterId = useCallback(() => {
    dispatch(
      appChatStateMutate((state) => {
        state.overrideCharacterId = null;
      })
    );
  }, [dispatch]);
  const onSelect = useCallback(
    (name: string, id: string) => {
      dispatch(
        appStateMutate((state) => {
          state.roomChatName = name;
          state.roomChatId = id;
        })
      );
      onClearoverrideCharacterId();
      setOpen(false);
    },
    [setOpen, dispatch, onClearoverrideCharacterId]
  );

  return (
    <>
      <Popover open={open} anchorEl={anchorEl.current} onClose={onClose}>
        <List disablePadding>
          {activeCharacterIds.map((characterId) => (
            <CharacterNameSelectItem
              key={characterId}
              characterId={characterId}
              onSelect={onSelect}
              isActive
            />
          ))}
          {inactiveCharacterIds.length > 0 && activeCharacterIds.length > 0 && (
            <Divider variant="middle" />
          )}
          {inactiveCharacterIds.map((characterId) => (
            <CharacterNameSelectItem
              key={characterId}
              characterId={characterId}
              onSelect={onSelect}
            />
          ))}
        </List>
      </Popover>
      <Container ref={anchorEl}>
        <Tooltip title={t("キャラクター選択")}>
          <SquareAvatar onClick={onOpen} disabled={isRoleAudience}>
            {currentRoomChatCharacter ? (
              <Avatar
                variant="square"
                src={
                  toCDNUrl(currentRoomChatCharacter.iconUrl) || "/ccfolia.png"
                }
                imgProps={{ draggable: false }}
              />
            ) : (
              <FaceIcon />
            )}
          </SquareAvatar>
        </Tooltip>
        {/* <CharacterAutoComplete /> */}
        {isOverride ? (
          <NameTextBox>
            <NameText>{name}</NameText>
            <IconButton
              size="small"
              onClick={onClearoverrideCharacterId}
              sx={{
                color: theme.palette.text.secondary,
                marginLeft: "auto",
              }}
            >
              <Cancel fontSize="inherit" />
            </IconButton>
          </NameTextBox>
        ) : (
          <NameTextInput
            name="name"
            value={name}
            onChange={onChange}
            placeholder="noname"
            disabled={isRoleAudience}
          />
        )}
      </Container>
    </>
  );
};

const Container = styled.div`
  display: flex;
  width: 100%;
`;

const SquareAvatar = styled(ButtonBase)`
  &.MuiButtonBase-root {
    box-sizing: border-box;
    border: 1px solid rgba(0, 0, 0, 0.12);
    display: block;
    width: 40px;
    min-width: 40px;
    height: 40px;
    img {
      object-position: top;
      width: 100%;
      height: 100%;
    }
  }
`;

const NameTextInput = styled(InputBase)`
  /* dirty */
  margin-right: 8px;
  padding: 4px;
  background: rgba(0, 0, 0, 0.2);
  flex-grow: 1;
`;

const NameTextBox = styled(Box)`
  display: flex;
  margin-right: 8px;
  padding: 4px;
  width: 197px;
`;
const NameText = styled(Typography)`
  padding: 4px 0;
  max-width: 169px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export default memo(CharacterNameSelect);
